import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mountain, ArrowLeft } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { formatApiError } from "../lib/api";
import { BRAND } from "../lib/brand";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1680508128888-505d636574ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHx0b3AlMjBkb3duJTIwbW91bnRhaW4lMjB0cmFpbHxlbnwwfHx8fDE3Nzc3Njk5NTN8MA&ixlib=rb-4.1.0&q=85";

export default function Signup() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", password: "" });
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!acceptedTerms) {
            toast.error("Please accept the Terms, Privacy Policy, and Community Guidelines to continue.");
            return;
        }
        setLoading(true);
        try {
            const u = await register(form.email, form.password, form.name, true);
            toast.success("Welcome to RealWorld Outdoors");
            navigate(u?.onboarding_completed ? "/feed" : "/onboarding");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-alpine-mist">
            <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
                <div className="w-full max-w-md">
                    <Link to="/" className="lg:hidden inline-flex items-center gap-2 mb-8">
                        <div className="w-9 h-9 rounded-md bg-midnight-pine flex items-center justify-center">
                            <Mountain className="w-5 h-5 text-ember-orange" />
                        </div>
                        <span className="font-display font-black text-xl text-midnight-pine">RealWorld Outdoors</span>
                    </Link>
                    <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight mb-2">
                        Join free
                    </h1>
                    <p className="text-slate-gray mb-8">
                        Already out there? <Link to="/login" data-testid="signup-to-login" className="text-ember-orange font-semibold hover:underline">Log in</Link>
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-2 block">Name</label>
                            <input
                                data-testid="signup-name-input"
                                required
                                value={form.name}
                                onChange={set("name")}
                                className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                                placeholder="Sierra Bennett"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-2 block">Email</label>
                            <input
                                data-testid="signup-email-input"
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                                placeholder="you@trailhead.com"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-2 block">Password</label>
                            <input
                                data-testid="signup-password-input"
                                type="password"
                                required
                                minLength={6}
                                value={form.password}
                                onChange={set("password")}
                                className="w-full px-4 py-3 rounded-md bg-snowcap border border-ridge-line focus:border-ember-orange focus:outline-none focus:ring-2 focus:ring-ember-orange/20"
                                placeholder="At least 6 characters"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="signup-submit-btn"
                            disabled={loading || !acceptedTerms}
                            className="w-full py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                        >
                            {loading ? "Creating…" : "Create account"}
                        </button>
                    </form>

                    <label
                        htmlFor="terms-accept"
                        data-testid="signup-terms-label"
                        className="flex items-start gap-3 mt-5 cursor-pointer select-none"
                    >
                        <input
                            id="terms-accept"
                            data-testid="signup-terms-checkbox"
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                            className="mt-1 w-4 h-4 rounded border-ridge-line text-ember-orange focus:ring-ember-orange/30 cursor-pointer"
                        />
                        <span className="text-xs text-slate-gray leading-relaxed">
                            I confirm I am at least <strong>18 years old</strong> and I agree to the{" "}
                            <Link to="/terms" target="_blank" rel="noopener" className="text-ember-orange font-semibold hover:underline">Terms of Service</Link>,{" "}
                            <Link to="/privacy" target="_blank" rel="noopener" className="text-ember-orange font-semibold hover:underline">Privacy Policy</Link>, and{" "}
                            <Link to="/community-guidelines" target="_blank" rel="noopener" className="text-ember-orange font-semibold hover:underline">Community Guidelines</Link>{" "}
                            (v{BRAND.legal.termsVersion}).
                        </span>
                    </label>

                    <p className="text-xs text-slate-gray mt-4 text-center">
                        Outdoor activities are inherently risky. Please review the assumption-of-risk section in our Terms before posting trip reports or using GPS routes.
                    </p>
                </div>
            </div>

            <div className="hidden lg:block relative order-1 lg:order-2">
                <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-midnight-pine/55" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <Link to="/" data-testid="signup-back-home" className="inline-flex items-center gap-2 text-sm font-bold">
                        <ArrowLeft className="w-4 h-4" /> Back to home
                    </Link>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-trail-tan mb-3">Join the crew</div>
                        <h2 className="font-display font-black text-5xl leading-[0.95] tracking-tighter">
                            First 12k<br />routes are<br />
                            <span className="text-ember-orange">on the house.</span>
                        </h2>
                    </div>
                </div>
            </div>
        </div>
    );
}
